import templateV3 from './template.v3.json' with { type: "json" };
import packageInfo from '../package.json' with { type: "json" };
import { binToHex, binToUtf8, cashAddressToLockingBytecode, createVirtualMachineBch2026, generateTransaction, hexToBin, stringify, verifyTransactionTokens } from '@bitauth/libauth';
import { getLibauthCompiler, getWalletLayers } from '@unspent/tau';
import { decodePushBytes, getTransactionFees, getScriptHash, valueDifference } from '@unspent/tau';
export default class Trust {
    static USER_AGENT = packageInfo.name;
    static PROTOCOL_IDENTIFIER = "U3P";
    static VERSION = "1.0.0";
    static tokenAware = false;
    static template = templateV3;
    static compiler = getLibauthCompiler(this.template);
    static vm = createVirtualMachineBch2026();
    //static PERIOD = 1;
    static PERIOD = 4383;
    static INSTALLMENT_DENOMINATOR = 100;
    static RETURN_NUMERATOR = 9_899;
    static RETURN_DENOMINATOR = 10_000;
    static dataToBytecode(data) {
        return {
            "recipient": hexToBin(data.recipient)
        };
    }
    static bytecodeToData(bytecode) {
        return {
            "recipient": binToHex(bytecode['recipient']),
        };
    }
    static parseCommitment(record) {
        if (typeof record === "string")
            record = hexToBin(record);
        const decodedData = decodePushBytes(record);
        if (binToUtf8(decodedData[0]) !== this.PROTOCOL_IDENTIFIER)
            throw Error(`"Non-${this.name} record NFT passed as ${this.name}"`);
        let byteData = {
            "recipient": decodedData[1]
        };
        return byteData;
    }
    static encodeCommitment(data) {
        const bytecode = this.dataToBytecode(data);
        return binToHex(this.getLockingBytecode(bytecode));
    }
    static getLockingBytecode(data) {
        const lockingBytecodeResult = this.compiler.generateBytecode({
            data: {
                "bytecode": data
            },
            scriptId: 'lock'
        });
        if (!lockingBytecodeResult.success) {
            /* c8 ignore next */
            throw new Error('Failed to generate bytecode, script: , ' + stringify(lockingBytecodeResult));
        }
        return lockingBytecodeResult.bytecode;
    }
    /**
       * Get getScriptHash
       *
       * @param record - a utxo parameters of the subscription.
       * @param reversed - whether to reverse the hash.
       * @throws {Error} if transaction generation fails.
       * @returns a cashaddress.
       */
    static getScriptHash(record, reversed = true) {
        return getScriptHash(this.getLockingBytecode(this.parseCommitment(record)), reversed);
    }
    /**
       * Get cashaddress
       *
       * @throws {Error} Throws no address error.
       * @returns a cashaddress.
       */
    static getAddress() {
        throw Error("No Cashaddr for p2s contract type" /* UnspentError.NoCashAddrForp2s */);
    }
    static getSourceOutput(data, utxo) {
        return {
            lockingBytecode: this.getLockingBytecode(data),
            valueSatoshis: BigInt(utxo.value)
        };
    }
    static getInstallmentOutput(data, utxo) {
        let outputValue = Math.round((utxo.value) / this.INSTALLMENT_DENOMINATOR);
        return {
            lockingBytecode: data["recipient"],
            valueSatoshis: BigInt(outputValue),
        };
    }
    static getDepositOutput(data, amount) {
        return {
            lockingBytecode: {
                data: {
                    "bytecode": data
                },
                compiler: this.compiler,
                script: 'lock'
            },
            valueSatoshis: BigInt(amount),
        };
    }
    static getReturnOutput(data, utxo) {
        let outputValue = Math.round((utxo.value * this.RETURN_NUMERATOR) / this.RETURN_DENOMINATOR);
        return {
            lockingBytecode: {
                data: {
                    "bytecode": data
                },
                compiler: this.compiler,
                script: 'lock'
            },
            valueSatoshis: BigInt(outputValue),
        };
    }
    static getExecutorOutput(cashaddr, amount) {
        let lockingBytecode = cashAddressToLockingBytecode(cashaddr);
        if (typeof lockingBytecode == "string")
            throw lockingBytecode;
        return {
            lockingBytecode: lockingBytecode.bytecode,
            valueSatoshis: amount
        };
    }
    static getInput(data, utxo, sequenceNumberOverride = this.PERIOD) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: sequenceNumberOverride,
            unlockingBytecode: {
                data: {
                    "bytecode": data
                },
                compiler: this.compiler,
                script: 'unlock',
                valueSatoshis: BigInt(utxo.value),
            },
        };
    }
    static fund(amount, lockingBytecode, walletUtxos, privateKey, fee = 1) {
        const inputs = [];
        const outputs = [];
        let sourceOutputs = [];
        if (typeof lockingBytecode !== "string")
            lockingBytecode = binToHex(lockingBytecode);
        let data = this.dataToBytecode({ "recipient": lockingBytecode });
        let config = {
            locktime: 0,
            version: 2,
            inputs, outputs,
        };
        // amount
        config.outputs.push(this.getDepositOutput(data, amount));
        config = getWalletLayers(config, sourceOutputs, walletUtxos, privateKey);
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + stringify(result.errors));
        let transaction = result.transaction;
        const estimatedFee = getTransactionFees(result.transaction, fee);
        // subtract fees off the change output
        const outIdx = config.outputs.length - 1;
        config.outputs[outIdx].valueSatoshis = config.outputs[outIdx].valueSatoshis - estimatedFee;
        result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        transaction = result.transaction;
        const tokenValidationResult = verifyTransactionTokens(transaction, sourceOutputs, { maximumTokenCommitmentLength: 128 });
        if (tokenValidationResult !== true)
            throw tokenValidationResult;
        let verify = this.vm.verify({
            sourceOutputs: sourceOutputs,
            transaction: transaction,
        });
        if (typeof verify == "string")
            throw verify;
        return {
            sourceOutputs: sourceOutputs,
            transaction: transaction,
            verify: verify
        };
    }
    /**
       * Step annuity installments forward
       *
       * @param jobs - a list of trust records and utxos.
       * @param height - the current block height.
       * @param executorCashaddress - address to receive excess fees, if any.
       * @param sequenceNumberOverride - override the default period (dev only).
       * @throws {Error} if transaction generation fails.
       * @returns a cashaddress.
       */
    static execute(jobs, height, executorCashaddress, fee = 1) {
        const inputs = [];
        const outputs = [];
        let sourceOutputs = [];
        const config = {
            locktime: 0,
            version: 2,
            inputs, outputs,
        };
        // add sources, inputs, and installment outputs to the config
        for (let job of jobs) {
            let data = this.parseCommitment(job.record);
            // if the job is not in mempool
            if (job.utxo.height > 0) {
                const age = height - job.utxo.height;
                // if the subscription is ready to be processed
                if (age >= this.PERIOD) {
                    config.inputs.push(this.getInput(data, job.utxo, this.PERIOD));
                    config.outputs.push(this.getInstallmentOutput(data, job.utxo));
                    sourceOutputs.push(this.getSourceOutput(data, job.utxo));
                }
            }
        }
        // add principal return outputs
        for (let job of jobs) {
            let data = this.parseCommitment(job.record);
            // if the job is not in mempool
            if (job.utxo.height > 0) {
                const age = height - job.utxo.height;
                // if the subscription is ready to be processed
                if (age >= this.PERIOD) {
                    config.outputs.push(this.getReturnOutput(data, job.utxo));
                }
            }
        }
        if (executorCashaddress) {
            let executorBonus = -valueDifference(sourceOutputs, config.outputs);
            console.log(executorBonus);
            if (executorBonus > 0) {
                config.outputs.push(this.getExecutorOutput(executorCashaddress, executorBonus));
            }
        }
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + stringify(result.errors));
        let transaction = result.transaction;
        if (executorCashaddress) {
            const estimatedFee = getTransactionFees(result.transaction, fee) + 1n;
            console.log(estimatedFee);
            const lastIdx = config.outputs.length - 1;
            config.outputs[lastIdx].valueSatoshis = config.outputs[lastIdx].valueSatoshis - estimatedFee;
        }
        result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        transaction = result.transaction;
        let verify = this.vm.verify({
            sourceOutputs: sourceOutputs,
            transaction: transaction,
        });
        if (typeof verify == "string")
            throw verify;
        return {
            sourceOutputs: sourceOutputs,
            transaction: transaction,
            verify: verify
        };
    }
}
//# sourceMappingURL=index.js.map